import { debug } from '../utils/debug';
// Real Stripe configuration
export const STRIPE_CONFIG = {
  // Your platform's publishable key from Stripe Dashboard
  publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_actual_publishable_key_here',

  // API base URL for your backend
  // Use relative URL to ensure it works with Vercel preview deployments
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  
  // Merchant-specific configuration
  merchants: {
    rideshare: {
      // Use environment variable if available, otherwise placeholder
      connectedAccountId: import.meta.env.VITE_STRIPE_RIDESHARE_ACCOUNT_ID || 'acct_your_rideshare_connected_account_id',
      platformFeePercentage: 0.02, // 2%
      deposits: {
        enabled: true,
        rules: [
          { category: 'Electric Bikes', amount: 100, currency: 'USD' },
          { category: '*', amount: 50, currency: 'USD' }
        ]
      },
      offSessionPayments: false
    },
    cyclecentral: {
      // Use environment variable if available, otherwise placeholder
      connectedAccountId: import.meta.env.VITE_STRIPE_CYCLECENTRAL_ACCOUNT_ID || 'acct_your_cyclecentral_connected_account_id',
      platformFeePercentage: 0.03, // 3%
      deposits: {
        enabled: false,
        rules: []
      },
      offSessionPayments: true
    }
  }
};

// Helper function to get merchant Stripe config
export const getMerchantStripeConfig = (merchantId) => {
  return STRIPE_CONFIG.merchants[merchantId] || STRIPE_CONFIG.merchants.rideshare;
};

// Calculate deposit amount based on selected products
// depositRules: optional array from DB merchants.deposit_rules [{amount (cents), category_slug}]
export const calculateDepositAmount = (merchantId, selectedProducts, depositRules = null) => {
  // If DB deposit rules are provided, use them (amounts are in cents)
  if (depositRules && depositRules.length > 0) {
    let totalDeposit = 0;
    selectedProducts.forEach(product => {
      const quantity = product.quantity;
      const slug = product.category_slug || '';
      // Match by category_slug, fall back to wildcard '*'
      const rule = depositRules.find(r => r.category_slug === slug) ||
                   depositRules.find(r => r.category_slug === '*');
      if (rule) {
        totalDeposit += (rule.amount / 100) * quantity; // Convert cents to currency units
      }
    });
    return totalDeposit;
  }

  // Fallback: hardcoded config (legacy)
  const config = getMerchantStripeConfig(merchantId);

  if (!config.deposits.enabled) {
    return 0;
  }

  let totalDeposit = 0;
  selectedProducts.forEach(product => {
    const quantity = product.quantity;

    // Find matching deposit rule
    const rule = config.deposits.rules.find(r =>
      r.category === product.category || r.category === '*'
    );

    if (rule) {
      totalDeposit += rule.amount * quantity;
    }
  });

  return totalDeposit;
};

// Calculate platform fee
export const calculatePlatformFee = (merchantId, amount) => {
  const config = getMerchantStripeConfig(merchantId);
  return Math.round(amount * config.platformFeePercentage * 100) / 100;
};

// API helper functions with better error handling
// merchantConfig: optional object from database with { merchant_type, stripe_connected_account_id, deposits_enabled, platform_fee_percentage }
export const createPaymentIntent = async (merchantId, bookingData, totalAmount, merchantCurrency = 'usd', offSessionPaymentsOverride = null, merchantConfig = null, customerEmail = null) => {
  debug.log('Creating payment intent...', {
    merchantId,
    totalAmount,
    currency: merchantCurrency,
    apiUrl: STRIPE_CONFIG.apiBaseUrl,
    merchantConfig: merchantConfig ? {
      merchant_type: merchantConfig.merchant_type,
      has_connected_account: Boolean(merchantConfig.stripe_connected_account_id),
      deposits_enabled: merchantConfig.deposits_enabled
    } : 'using hardcoded config'
  });

  const hardcodedConfig = getMerchantStripeConfig(merchantId);

  // Determine connected account ID:
  // - Platform merchants (merchant_type = 'platform') should NOT use connected accounts
  // - Partner merchants use stripe_connected_account_id from database if available
  let connectedAccountId = null;
  if (merchantConfig) {
    // Use database values
    if (merchantConfig.merchant_type === 'partner' && merchantConfig.stripe_connected_account_id) {
      connectedAccountId = merchantConfig.stripe_connected_account_id;
    }
    // For 'platform' type, connectedAccountId stays null
  } else {
    // Fall back to hardcoded config (legacy behavior)
    connectedAccountId = hardcodedConfig.connectedAccountId;
  }

  // Determine if deposits are enabled
  const depositsEnabled = merchantConfig?.deposits_enabled ?? hardcodedConfig.deposits?.enabled ?? false;

  // Calculate deposit amount only if deposits are enabled
  // Pass DB deposit_rules for correct per-category amounts
  const depositRules = merchantConfig?.deposit_rules || null;
  const depositAmount = depositsEnabled ? calculateDepositAmount(merchantId, bookingData.selectedProducts, depositRules) : 0;

  // Calculate platform fee (only for partner merchants with connected accounts)
  // Uses additive model: round(total_cents * pct / 100) + fixed_cents
  // Database stores platform_fee_percentage as a whole number (e.g. 2 = 2%).
  let platformFee = 0;
  if (connectedAccountId) {
    const totalCents = Math.round(totalAmount * 100);
    const pct = merchantConfig?.platform_fee_percentage ?? (hardcodedConfig.platformFeePercentage ? hardcodedConfig.platformFeePercentage * 100 : 2);
    const fixedCents = merchantConfig?.platform_fee_fixed_cents ?? 0;
    const pctPart = Math.round(totalCents * pct / 100);
    platformFee = Math.max(0, pctPart + fixedCents) / 100;
  }

  // Use database value if provided, otherwise fall back to config value
  const offSessionPayments = offSessionPaymentsOverride !== null ? offSessionPaymentsOverride : hardcodedConfig.offSessionPayments;

  // Convert currency code to lowercase (Stripe requires lowercase)
  const currencyCode = (merchantCurrency || 'usd').toLowerCase();

  // Stripe PaymentIntent offers card only. PayPal is handled exclusively via the
  // direct (non-Stripe) integration, so it never goes in payment_method_types.
  const paymentMethods = ['card'];

  debug.log('Payment intent parameters:', {
    connectedAccountId: connectedAccountId || 'none (platform merchant)',
    depositsEnabled,
    depositAmount,
    platformFee,
    offSessionPayments,
    paymentMethods
  });

  try {
    const response = await fetch(`${STRIPE_CONFIG.apiBaseUrl}/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: Math.round(totalAmount * 100), // Convert to cents
        currency: currencyCode,
        merchantId,
        connectedAccountId: connectedAccountId, // null for platform merchants
        platformFee: Math.round(platformFee * 100), // Convert to cents (0 for platform merchants)
        depositAmount: Math.round(depositAmount * 100), // Convert to cents (0 if deposits disabled)
        bookingData,
        offSessionPayments: offSessionPayments,
        paymentMethodTypes: paymentMethods, // card only (PayPal handled via direct integration)
        customerEmail: customerEmail || null // Customer email for Stripe receipt and connected account visibility
      }),
    });

    debug.log('API Response status:', response.status);

    if (!response.ok) {
      // Try to parse error response as JSON first
      let errorMessage = `API Error (${response.status})`;
      try {
        const errorData = await response.json();
        // Use the message field if available, otherwise use error field
        errorMessage = errorData.message || errorData.error || errorMessage;
        console.error('API Error Response:', errorData);
      } catch (e) {
        // If JSON parsing fails, fall back to text
        const errorText = await response.text();
        errorMessage = errorText || errorMessage;
        console.error('API Error Response (text):', errorText);
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    debug.log('Payment intent created successfully:', data);
    return data;

  } catch (error) {
    console.error('Error creating payment intent:', error);
    
    // More specific error messages
    if (error.message.includes('fetch') || error.name === 'TypeError') {
      throw new Error('Unable to connect to payment server. Please check your internet connection.');
    }
    
    throw new Error(`Payment initialization failed: ${error.message}`);
  }
};

// Update an existing payment intent (e.g. when coupon is applied/removed after creation)
export const updatePaymentIntent = async (paymentIntentId, totalAmount, merchantId, merchantConfig = null, customerEmail = null) => {
  const hardcodedConfig = getMerchantStripeConfig(merchantId);

  // Determine connected account ID (same logic as createPaymentIntent)
  let connectedAccountId = null;
  if (merchantConfig) {
    if (merchantConfig.merchant_type === 'partner' && merchantConfig.stripe_connected_account_id) {
      connectedAccountId = merchantConfig.stripe_connected_account_id;
    }
  } else {
    connectedAccountId = hardcodedConfig.connectedAccountId;
  }

  // Calculate platform fee — additive model: round(total_cents * pct / 100) + fixed_cents
  let platformFee = 0;
  if (connectedAccountId) {
    const totalCents = Math.round(totalAmount * 100);
    const pct = merchantConfig?.platform_fee_percentage ?? (hardcodedConfig.platformFeePercentage ? hardcodedConfig.platformFeePercentage * 100 : 2);
    const fixedCents = merchantConfig?.platform_fee_fixed_cents ?? 0;
    const pctPart = Math.round(totalCents * pct / 100);
    platformFee = Math.max(0, pctPart + fixedCents) / 100;
  }

  debug.log('🔄 [STRIPE] Updating payment intent:', {
    paymentIntentId,
    newAmount: totalAmount,
    platformFee,
    connectedAccountId: connectedAccountId || 'none'
  });

  try {
    const response = await fetch(`${STRIPE_CONFIG.apiBaseUrl}/update-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentIntentId,
        amount: Math.round(totalAmount * 100), // Convert to cents
        platformFee: connectedAccountId ? Math.round(platformFee * 100) : null, // Only for connected accounts
        customerEmail: customerEmail || null
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `API Error (${response.status})`);
    }

    const data = await response.json();
    debug.log('✅ [STRIPE] Payment intent updated successfully:', data);
    return data;

  } catch (error) {
    console.error('❌ [STRIPE] Error updating payment intent:', error);
    throw new Error(`Payment update failed: ${error.message}`);
  }
};

export const confirmPayment = async (paymentIntentId, paymentMethodId) => {
  const response = await fetch(`${STRIPE_CONFIG.apiBaseUrl}/confirm-payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      paymentIntentId,
      paymentMethodId
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to confirm payment');
  }

  return response.json();
};
